import { useEffect, useState } from "react";
import { Sparkle, Printer, Trash, FilmSlate, BookOpen, UsersThree, Brain, ListChecks, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const TONES = ["Plain talk", "Warm but direct", "Faith-influenced", "No corporate jargon", "Coaching / encouraging", "Formal & regulatory"];

const VOICES = [
  { id: "nova", label: "Nova — energetic, upbeat" },
  { id: "shimmer", label: "Shimmer — bright, cheerful" },
  { id: "coral", label: "Coral — warm, friendly" },
  { id: "sage", label: "Sage — wise, measured" },
  { id: "onyx", label: "Onyx — deep, authoritative" },
  { id: "alloy", label: "Alloy — neutral, balanced" },
  { id: "ash", label: "Ash — clear, articulate" },
  { id: "echo", label: "Echo — smooth, calm" },
  { id: "fable", label: "Fable — expressive" },
];

const SECTIONS = [
  { key: "print_script", label: "Print script (facilitator)", icon: BookOpen, hint: "Read-aloud script for an instructor-led session" },
  { key: "instructor_guide", label: "Instructor's 10-minute live guide", icon: UsersThree, hint: "Quick interactive session plan for Zoom or in-person — timings, questions to ask, one activity" },
  { key: "video_shotlist", label: "Video shot-list", icon: FilmSlate, hint: "Scene-by-scene: narration + on-screen cues" },
  { key: "exercises", label: "Instructor exercises", icon: UsersThree, hint: "Practice and discussion activities" },
  { key: "underpinnings", label: "Why it matters (the human side)", icon: Brain, hint: "What the behavior means for the client — e.g. being late isn't just being late; it breaks trust and keeps the person on guard" },
];

export default function TrainingCreatorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [topics, setTopics] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [packs, setPacks] = useState([]);
  const [topicIds, setTopicIds] = useState([]);
  const [policyIds, setPolicyIds] = useState([]);
  const [customSubject, setCustomSubject] = useState("");
  const [tones, setTones] = useState(["Plain talk", "Warm but direct", "No corporate jargon"]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [pack, setPack] = useState(null);
  const [attachTopic, setAttachTopic] = useState("");
  const [narrSection, setNarrSection] = useState("print_script");
  const [narrVoice, setNarrVoice] = useState("nova");
  const [narrBusy, setNarrBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [readingLink, setReadingLink] = useState("");
  const [feedback, setFeedback] = useState("");
  const [revBusy, setRevBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  const runAiReview = async () => {
    if (!pack?.id) return;
    setReviewBusy(true);
    try {
      await api.post(`/training-creator/packs/${pack.id}/ai-review`);
      toast.success("Claude is reading the whole pack — this takes about a minute");
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get(`/training-creator/packs/${pack.id}`);
          setPack(data.pack);
          const st = data.pack.ai_review_status;
          if (st === "ready") { toast.success("Independent review is in — Claude read the whole pack"); setReviewBusy(false); return; }
          if (st === "error") { toast.error(data.pack.ai_review_error || "Review failed — try again"); setReviewBusy(false); return; }
        } catch { /* keep polling */ }
        if (Date.now() - started < 180000) setTimeout(poll, 8000);
        else { toast.error("Review is taking unusually long — reopen the pack in a minute"); setReviewBusy(false); }
      };
      setTimeout(poll, 8000);
    } catch (e) { toast.error(formatApiError(e)); setReviewBusy(false); }
  };

  const revisePack = async () => {
    if (!pack?.id) return;
    if (feedback.trim().length < 10) { toast.error("Write your feedback first — what should change?"); return; }
    if (!window.confirm("Revise this pack with your feedback? The current version is saved in the pack's revision history first.")) return;
    setRevBusy(true);
    try {
      const { data } = await api.post(`/training-creator/packs/${pack.id}/revise`, { feedback });
      toast.success(`Revised: ${(data.revised || []).join(", ")}${data.stale?.length ? ` — regenerate ${data.stale.join(" and ")} to match` : ""}`);
      setFeedback("");
      await openPack(pack.id);
    } catch (e) { toast.error(formatApiError(e)); }
    setRevBusy(false);
  };

  const generateImages = async () => {
    if (!pack?.id) return;
    setImgBusy(true);
    try {
      await api.post(`/training-creator/packs/${pack.id}/images`);
      toast.success("Images generating — they take about a minute each. Reopen the pack in ~2 minutes.");
      setTimeout(() => openPack(pack.id), 90000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImgBusy(false); }
  };

  const generateGuide = async () => {
    if (!pack?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/training-creator/packs/${pack.id}/instructor-guide`);
      setPack((p) => ({ ...p, instructor_guide: data.instructor_guide }));
      toast.success("Instructor's 10-minute guide generated — review it below");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!pack?.id) return;
    try {
      const r = await api.get(`/training-creator/packs/${pack.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `training-pack-${(pack.title || "pack").replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const downloadDeck = async () => {
    if (!pack?.id) return;
    try {
      const r = await api.get(`/training-creator/packs/${pack.id}/deck`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deck-${(pack.title || "training").replace(/\s+/g, "-")}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Generate the slides first"); }
  };

  const generateSlides = async () => {
    if (!pack?.id) return;
    setImgBusy(true);
    try {
      await api.post(`/training-creator/packs/${pack.id}/slides`);
      toast.success("Slides generating — text and an image per screen. Reopen in ~3 minutes.");
      setTimeout(() => openPack(pack.id), 150000);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImgBusy(false); }
  };

  const publishReading = async () => {
    if (!pack?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/training-creator/packs/${pack.id}/publish-reading`, {});
      setReadingLink(`${window.location.origin}/train/${data.topic.token}`);
      toast.success("Published as a read-and-sign training — staff can read or listen, quiz, and sign", { duration: 6000 });
      loadPacks();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const makeNarration = async () => {
    if (!pack?.id) return;
    setNarrBusy(true);
    try {
      await api.post(`/training-creator/packs/${pack.id}/narration`, { section: narrSection, voice: narrVoice });
      toast.success("Voiceover generating — usually under a minute. The player will appear here when it's ready.");
      setTimeout(() => openPack(pack.id), 25000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNarrBusy(false);
    }
  };

  const [heyAvatars, setHeyAvatars] = useState(null);
  const [heyVoices, setHeyVoices] = useState(null);
  const [heyAvatarId, setHeyAvatarId] = useState("");
  const [heyVoiceId, setHeyVoiceId] = useState("");
  const [heyBusy, setHeyBusy] = useState(false);
  const [pubBusy, setPubBusy] = useState("");

  const publishMedia = async (media) => {
    if (!pack?.id) return;
    const tid = attachTopic || pack.topic_id || pack.reading_topic_id;
    if (!tid) { toast.error("Choose the training topic first — attach dropdown at the bottom of this pack"); return; }
    setPubBusy(media);
    try {
      const { data } = await api.post(`/training-creator/packs/${pack.id}/publish-media`, { topic_id: tid, media, section: narrSection });
      toast.success(`${media === "video" ? "Presenter video" : media === "both" ? "Voiceover + video" : "Voiceover"} now plays inside "${data.topic_title}" — staff get it when they open the training`);
    } catch (e) { toast.error(formatApiError(e)); }
    setPubBusy("");
  };

  const loadHeygen = () => {
    if (heyAvatars !== null) return;
    api.get("/training-creator/heygen/avatars").then((r) => setHeyAvatars(r.data.avatars || [])).catch(() => setHeyAvatars([]));
    api.get("/training-creator/heygen/voices").then((r) => setHeyVoices(r.data.voices || [])).catch(() => setHeyVoices([]));
  };

  const makeAvatarVideo = async () => {
    if (!pack?.id || !heyAvatarId) { toast.error("Pick an avatar first"); return; }
    setHeyBusy(true);
    try {
      await api.post(`/training-creator/packs/${pack.id}/avatar-video`, { avatar_id: heyAvatarId, voice_id: heyVoiceId || null, section: narrSection });
      toast.success("Avatar video rendering — roughly 5–10 minutes per minute of video. The player will appear here when it's done.");
      setTimeout(() => openPack(pack.id), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHeyBusy(false);
    }
  };

  const loadPacks = () => api.get("/training-creator/packs").then((r) => setPacks(r.data.packs || [])).catch(() => {});

  useEffect(() => {
    api.get("/training/topics").then((r) => setTopics(r.data || [])).catch(() => {});
    api.get("/policies").then((r) => setPolicies(r.data || [])).catch(() => {});
    loadPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; api/setTopics/setPolicies/loadPacks are stable
  }, []);

  const toggleTone = (t) => setTones((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const sourceCount = topicIds.length + policyIds.length + (customSubject.trim() ? 1 : 0);

  const toggleSource = (setter) => (id) => setter((cur) => {
    if (cur.includes(id)) return cur.filter((x) => x !== id);
    if (sourceCount >= 3) { toast.error("Up to 3 sources per pack — uncheck one first"); return cur; }
    return [...cur, id];
  });

  const generate = async () => {
    const subject = customSubject.trim();
    if (sourceCount === 0) { toast.error("Pick up to 3 sources, or type your own subject"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/training-creator/generate", {
        topic_ids: topicIds, policy_ids: policyIds, custom_subject: subject || null, tones, notes,
      }, { timeout: 30000 });
      setPack(data.pack);
      setAttachTopic(data.pack.topic_id || "");
      if (subject) {
        setCustomSubject("");
        api.get("/training/topics").then((r) => setTopics(r.data || [])).catch(() => {});
      }
      toast.success("Writing your pack — about a minute. It will appear here when ready.");
      loadPacks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (pack?.status !== "generating") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/training-creator/packs/${pack.id}`);
        if (data.pack.status !== "generating") {
          setPack(data.pack);
          if (data.pack.status === "error") toast.error(`Generation failed: ${data.pack.error || "unknown error"} — try again`);
          else toast.success("Training pack ready — review and edit every section before using it");
          loadPacks();
        }
      } catch (e) { console.debug("pack poll retry:", e); }
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pack?.id, pack?.status]);

  const openPack = async (id) => {
    try {
      const { data } = await api.get(`/training-creator/packs/${id}`);
      setPack(data.pack);
      setAttachTopic(data.pack.topic_id || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/training-creator/packs/${pack.id}`, {
        title: pack.title,
        print_script: pack.print_script, video_shotlist: pack.video_shotlist,
        exercises: pack.exercises, underpinnings: pack.underpinnings,
        instructor_guide: pack.instructor_guide,
      });
      toast.success("Saved");
      loadPacks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const attach = async () => {
    if (!attachTopic) { toast.error("Choose a training topic"); return; }
    try {
      await api.post(`/training-creator/packs/${pack.id}/attach`, { topic_id: attachTopic });
      toast.success("Attached to training topic");
      loadPacks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete pack "${pack.title}"?`)) return;
    try {
      await api.delete(`/training-creator/packs/${pack.id}`);
      toast.success("Pack deleted");
      setPack(null);
      loadPacks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [pushPolicy, setPushPolicy] = useState("");
  const [pushBusy, setPushBusy] = useState(false);
  const pushQuiz = async () => {
    const target = pushPolicy || pack.policy_id;
    if (!target) { toast.error("Choose a policy to receive the quiz"); return; }
    setPushBusy(true);
    try {
      const { data } = await api.post(`/training-creator/packs/${pack.id}/push-quiz`, { policy_id: target });
      toast.success(`Policy quiz now has ${data.quiz_count} questions — review under Policies → Sign-off (enable sign-off if it isn't already)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div data-testid="creator-page">
      <div className="mb-8 max-w-3xl">
        <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Training Creator</h1>
        <p className="mt-1 text-sm text-stone-500">
          AI drafts a complete training pack — print facilitator script, video shot-list, instructor exercises, and the human “why” behind the behavior —
          grounded in your policies, mission, values, and the voice of your existing scripts. AI drafts; you edit and approve. Nothing goes to staff until you say so.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6">
          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="creator-form">
            <p className="text-sm font-semibold text-stone-800">New training pack</p>
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label>Sources — pick up to 3 <span className="font-normal text-stone-400">({sourceCount}/3)</span></Label>
                <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border border-stone-200 bg-white p-2" data-testid="creator-topic-multi">
                  {topics.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-xs text-stone-700" data-testid={`creator-topic-${t.id}`}>
                      <input type="checkbox" checked={topicIds.includes(t.id)} onChange={() => toggleSource(setTopicIds)(t.id)} />
                      <span className="truncate">{t.title}</span>
                    </label>
                  ))}
                </div>
                <div className="max-h-28 space-y-1 overflow-y-auto rounded-md border border-stone-200 bg-white p-2" data-testid="creator-policy-multi">
                  {policies.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 text-xs text-stone-700" data-testid={`creator-policy-${p.id}`}>
                      <input type="checkbox" checked={policyIds.includes(p.id)} onChange={() => toggleSource(setPolicyIds)(p.id)} />
                      <span className="truncate">{p.title} <span className="text-stone-400">(policy)</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>…or type your own subject — generates on the fly and saves as a new topic</Label>
                <Input value={customSubject} onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder="e.g. Safe transfers for bed-bound clients"
                  className="border-stone-300 bg-white text-sm" data-testid="creator-custom-subject" />
              </div>
              <div className="space-y-1.5">
                <Label>Voice &amp; tone (pick any)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {TONES.map((t) => (
                    <button key={t} type="button" onClick={() => toggleTone(t)}
                      className={`rounded-full border px-2.5 py-1 text-xs font-medium ${tones.includes(t) ? "border-primary bg-accent text-accent-foreground" : "border-stone-300 bg-white text-stone-500 hover:border-stone-400"}`}
                      data-testid={`creator-tone-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Extra guidance (optional)</Label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. include a scenario about a client who refuses a bath"
                  className="border-stone-300 bg-white text-sm" data-testid="creator-notes" />
              </div>
              <Button onClick={generate} disabled={busy || !isAdmin} className="w-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="creator-generate">
                <Sparkle size={15} className="mr-1.5" /> {busy ? "Writing your pack — this takes about a minute…" : "Generate training pack"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="creator-pack-list">
            <p className="text-sm font-semibold text-stone-800">Your packs ({packs.length})</p>
            <div className="mt-3 space-y-2">
              {packs.length === 0 && <p className="text-xs text-stone-400">None yet — generate your first pack above.</p>}
              {packs.map((p) => (
                <button key={p.id} onClick={() => openPack(p.id)}
                  className={`block w-full rounded-md border px-3 py-2 text-left text-xs transition-colors ${pack?.id === p.id ? "border-primary bg-accent" : "border-stone-200 hover:bg-stone-50"}`}
                  data-testid={`creator-pack-${p.id}`}>
                  <span className="font-medium text-stone-800">{p.title}</span>
                  <span className="mt-0.5 block text-stone-400">{String(p.updated_at || p.created_at).slice(0, 10)}{p.topic_title ? ` · ${p.topic_title}` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {!pack ? (
            <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-stone-300 bg-white text-sm text-stone-400" data-testid="creator-empty">
              Pick a topic and generate a pack — or open one from the list.
            </div>
          ) : pack.status === "generating" ? (
            <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-stone-200 bg-white" data-testid="creator-generating">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-sm font-medium text-stone-600">Writing your training pack — about a minute…</p>
              <p className="text-xs text-stone-400">Grounding it in your policies, mission, values, and the voice of your scripts.</p>
            </div>
          ) : pack.status === "error" ? (
            <div className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/5 p-6" data-testid="creator-error">
              <p className="text-sm font-semibold text-[#C0392B]">Generation failed</p>
              <p className="mt-1 text-xs text-stone-500">{pack.error || "Unknown error"} — try again, or delete this pack.</p>
              <Button size="sm" variant="outline" onClick={remove} className="mt-3 border-[#C0392B]/40 text-[#C0392B]" data-testid="creator-error-delete">Delete pack</Button>
            </div>
          ) : (
            <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="creator-editor">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Input value={pack.title} onChange={(e) => setPack({ ...pack, title: e.target.value })}
                  className="max-w-md border-stone-300 bg-white font-heading text-lg font-semibold" data-testid="creator-title-input" />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={downloadPdf} className="border-stone-300" data-testid="creator-print">
                    <Printer size={14} className="mr-1" /> Printable PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={remove} className="border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid="creator-delete">
                    <Trash size={14} />
                  </Button>
                </div>
              </div>

              <div className="mt-3 rounded-md border border-primary/25 bg-primary/5 p-3" data-testid="creator-feedback">
                <p className="text-xs font-semibold text-stone-700">Your editorial review — the pack rewrites around it
                  {(pack.revisions || []).length > 0 && <span className="ml-2 font-normal text-stone-400">{(pack.revisions || []).length} earlier version(s) saved · last revised {String(pack.revised_at || "").slice(0, 10)}</span>}
                </p>
                <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={4}
                  placeholder={"What works, what doesn't, what to change — e.g. \"bones are good, keep the tone; fix the interpreter rule everywhere it appears; cut the crevices/shadows phrase\""}
                  className="mt-2 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="creator-feedback-input" />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={revisePack} disabled={revBusy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="creator-revise">
                    {revBusy ? "Revising…" : "Revise with this feedback"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={runAiReview} disabled={reviewBusy} className="rounded-full border-stone-300 text-xs" data-testid="creator-ai-review">
                    {reviewBusy ? "Reviewing (about a minute)…" : "Independent AI review (Claude)"}
                  </Button>
                  <span className="text-[11px] text-stone-400">Legal corrections are applied everywhere the topic appears — script, role-plays, recap, quiz. The old version is kept.</span>
                </div>
                {pack.ai_review?.text && (
                  <div className="mt-3 rounded-md border border-stone-200 bg-white p-3" data-testid="creator-ai-review-panel">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-stone-700">Independent review by Claude ({pack.ai_review.model}) · {String(pack.ai_review.at || "").slice(0, 10)}</p>
                      <button onClick={() => { const txt = pack.ai_review.text; const m = txt.search(/##\s*Legal/i); const actionable = m >= 0 ? txt.slice(m) : txt; setFeedback((f) => (f ? f + "\n\n" : "") + "Independent AI review (Claude) — apply the blocker-level corrections below ONLY. Keep the tone, structure, and everything else exactly as-is:\n\n" + actionable.replace(/##\s*Optional polish[\s\S]*?(?=##|$)/i, "")); toast.success("Blocker findings copied into the feedback box — hit Revise when ready"); }}
                        className="rounded-full border border-primary/40 px-2.5 py-0.5 text-[11px] font-medium text-primary hover:bg-primary hover:text-white" data-testid="creator-review-to-feedback">
                        Send findings to feedback box
                      </button>
                    </div>
                    <pre className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-stone-700">{pack.ai_review.text}</pre>
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="creator-narration">
                <p className="text-xs font-semibold text-stone-600">AI voiceover — turn the script into narration audio</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <select value={narrSection} onChange={(e) => setNarrSection(e.target.value)}
                    className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="creator-narration-section">
                    <option value="print_script">Print script</option>
                    <option value="video_shotlist">Video shot-list narration</option>
                  </select>
                  <select value={narrVoice} onChange={(e) => setNarrVoice(e.target.value)}
                    className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="creator-narration-voice">
                    {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={makeNarration} disabled={narrBusy} className="border-stone-300 text-xs" data-testid="creator-narration-generate">
                    {narrBusy ? "Generating…" : "Generate voiceover"}
                  </Button>
                  {pack.narration?.[narrSection]?.status === "generating" && (
                    <span className="text-[11px] text-stone-400" data-testid="creator-narration-working">Working on it — refresh in ~30s</span>
                  )}
                </div>
                {pack.narration?.[narrSection]?.status === "ready" && (
                  <audio controls className="mt-2 w-full" data-testid="creator-narration-player"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/training-creator/packs/${pack.id}/narration/${narrSection}?v=${pack.narration[narrSection].created_at || ""}`} />
                )}
                {pack.narration?.[narrSection]?.status === "error" && (
                  <p className="mt-2 text-[11px] text-[#C0392B]" data-testid="creator-narration-error">Voiceover failed: {pack.narration[narrSection].error}</p>
                )}
              </div>

              <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="creator-avatar-video">
                <p className="text-xs font-semibold text-stone-600">AI presenter video — a HeyGen avatar reads the training aloud (uses your HeyGen credit; render takes a few minutes)</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {!heyAvatars ? (
                    <Button size="sm" variant="outline" onClick={loadHeygen} className="border-stone-300 text-xs" data-testid="creator-heygen-load">Load avatars &amp; voices</Button>
                  ) : (
                    <>
                      <select value={heyAvatarId} onChange={(e) => setHeyAvatarId(e.target.value)}
                        className="h-8 max-w-56 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="creator-heygen-avatar">
                        <option value="">Choose avatar…</option>
                        <optgroup label="Your avatars">
                          {heyAvatars.filter((a) => a.kind === "yours").map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </optgroup>
                        <optgroup label="Avatar library">
                          {heyAvatars.filter((a) => a.kind !== "yours").map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </optgroup>
                      </select>
                      <select value={heyVoiceId} onChange={(e) => setHeyVoiceId(e.target.value)}
                        className="h-8 max-w-48 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="creator-heygen-voice">
                        <option value="">Avatar's default voice</option>
                        <optgroup label="Your voices (custom)">
                          {(heyVoices || []).filter((v) => v.kind === "custom").map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
                        </optgroup>
                        <optgroup label="Voice library">
                          {(heyVoices || []).filter((v) => v.kind !== "custom").map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}
                        </optgroup>
                      </select>
                      <Button size="sm" variant="outline" onClick={makeAvatarVideo} disabled={heyBusy || !heyAvatarId} className="border-stone-300 text-xs" data-testid="creator-heygen-generate">
                        {heyBusy ? "Starting…" : "Generate presenter video"}
                      </Button>
                    </>
                  )}
                  {["queued", "processing"].includes(pack.avatar_video?.status) && (
                    <span className="text-[11px] text-stone-400" data-testid="creator-heygen-working">Rendering — refresh in a few minutes</span>
                  )}
                </div>
                {pack.avatar_video?.status === "ready" && (
                  <video controls className="mt-2 w-full rounded-md" data-testid="creator-heygen-player"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/training-creator/packs/${pack.id}/avatar-video/file`} />
                )}
                {pack.avatar_video?.status === "error" && (
                  <p className="mt-2 text-[11px] text-[#C0392B]" data-testid="creator-heygen-error">Video failed: {pack.avatar_video.error}</p>
                )}
              </div>

              <div className="mt-3 rounded-md border border-primary/25 bg-primary/5 p-3" data-testid="creator-publish-media">
                <p className="text-xs font-semibold text-stone-700">Connect to the staff training — put this pack's voiceover and/or presenter video on the training topic staff actually take</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => publishMedia("voiceover")} disabled={!!pubBusy} className="rounded-full border-stone-300 text-xs" data-testid="creator-publish-voiceover">
                    {pubBusy === "voiceover" ? "Publishing…" : "Use voiceover on training"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => publishMedia("video")} disabled={!!pubBusy} className="rounded-full border-stone-300 text-xs" data-testid="creator-publish-video">
                    {pubBusy === "video" ? "Publishing…" : "Use presenter video on training"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => publishMedia("both")} disabled={!!pubBusy} className="rounded-full border-stone-300 text-xs" data-testid="creator-publish-both">
                    {pubBusy === "both" ? "Publishing…" : "Both"}
                  </Button>
                </div>
                <p className="mt-1.5 text-[11px] text-stone-400">
                  Goes to the topic chosen in the attach dropdown below{pack.topic_title ? ` (currently: ${pack.topic_title})` : ""}.
                  Staff see the narrated lesson above the reading and the video at the top of the training — no separate link to lose.
                </p>
              </div>

              {(pack.source_citations || []).length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="creator-citations">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Built from:</span>
                  {pack.source_citations.map((c, i) => (
                    <span key={i} className="rounded-full border border-primary/30 bg-accent px-2.5 py-0.5 text-[11px] font-medium text-accent-foreground" data-testid={`creator-citation-${i}`}>
                      {c.title}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="creator-images">
                <p className="text-xs font-semibold text-stone-600">Training illustrations — AI images printed inside the PDF handout</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={generateImages} disabled={imgBusy || pack.images_status === "generating"} className="border-stone-300 text-xs" data-testid="creator-images-generate">
                    <ImageIcon size={13} className="mr-1" /> {imgBusy || pack.images_status === "generating" ? "Generating…" : (pack.images || []).length ? "Regenerate images" : "Generate images"}
                  </Button>
                  {pack.images_status === "stale" && <span className="text-[11px] font-medium text-[#8a5d14]" data-testid="creator-images-stale">Content was revised — regenerate images to match</span>}
                  {!pack.instructor_guide && (
                    <Button size="sm" variant="outline" onClick={generateGuide} disabled={busy} className="border-stone-300 text-xs" data-testid="creator-guide-generate">
                      Generate instructor's 10-minute guide
                    </Button>
                  )}
                  {pack.images_status === "error" && <span className="text-[11px] text-[#C0392B]" data-testid="creator-images-error">Image generation failed{pack.images_error ? `: ${pack.images_error}` : ""}</span>}
                </div>
                {(pack.images || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2" data-testid="creator-images-grid">
                    {pack.images.map((img, i) => (
                      <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}/api/training-creator/packs/${pack.id}/images/${i}`} alt={img.caption || "Training illustration"}
                        className="aspect-video w-full rounded-md border border-stone-200 object-cover" data-testid={`creator-image-${i}`} />
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3" data-testid="creator-slides">
                <p className="text-xs font-semibold text-stone-600">Presentation screens — a slide per key point, image + big text (for Zoom or projector)</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={generateSlides} disabled={imgBusy || pack.slides_status === "generating"} className="border-stone-300 text-xs" data-testid="creator-slides-generate">
                    {imgBusy || pack.slides_status === "generating" ? "Generating…" : (pack.slides || []).length ? "Regenerate slides" : "Generate slides"}
                  </Button>
                  {(pack.slides || []).length > 0 && (
                    <Button size="sm" variant="outline" onClick={downloadDeck} className="border-stone-300 text-xs" data-testid="creator-deck-download">
                      Download slide deck (.pptx)
                    </Button>
                  )}
                  {pack.slides_status === "stale" && <span className="text-[11px] font-medium text-[#8a5d14]" data-testid="creator-slides-stale">Content was revised — regenerate slides to match</span>}
                  {pack.slides_status === "error" && <span className="text-[11px] text-[#C0392B]" data-testid="creator-slides-error">Slide generation failed{pack.slides_error ? `: ${pack.slides_error}` : ""}</span>}
                </div>
                {(pack.slides || []).length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2" data-testid="creator-slides-grid">
                    {pack.slides.map((sl, i) => (
                      <div key={i} className="overflow-hidden rounded-md border border-stone-200 bg-white" data-testid={`creator-slide-${i}`}>
                        {sl.image_path ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/training-creator/packs/${pack.id}/slides/${i}/image`} alt={sl.title}
                            className="aspect-video w-full object-cover" />
                        ) : (
                          <div className="flex aspect-video w-full items-center justify-center bg-stone-100 text-[11px] text-stone-400">no image</div>
                        )}
                        <div className="p-2">
                          <p className="text-xs font-semibold text-stone-800">{i + 1}. {sl.title}</p>
                          <p className="mt-0.5 text-[11px] text-stone-500">{sl.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-5 space-y-5">
                {SECTIONS.map((s) => (
                  <div key={s.key} className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><s.icon size={15} className="text-primary" /> {s.label}</Label>
                    <p className="text-[11px] text-stone-400">{s.hint}</p>
                    <Textarea rows={10} value={pack[s.key] || ""} onChange={(e) => setPack({ ...pack, [s.key]: e.target.value })}
                      className="border-stone-300 bg-white text-sm leading-relaxed" data-testid={`creator-section-${s.key}`} />
                  </div>
                ))}

                {(pack.quiz_questions || []).length > 0 && (
                  <div className="rounded-md border border-stone-200 bg-stone-50 p-4" data-testid="creator-quiz">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-stone-500">
                      <ListChecks size={14} /> Suggested quiz questions ({pack.quiz_questions.length})
                    </p>
                    <div className="mt-2 space-y-3">
                      {pack.quiz_questions.map((q, i) => (
                        <div key={`${i}-${(q.q || "").slice(0, 24)}`} className="text-xs" data-testid={`creator-quiz-q-${i}`}>
                          <p className="font-semibold text-stone-700">{i + 1}. {q.q}</p>
                          <ul className="mt-1 list-disc pl-5 text-stone-600">
                            {(q.choices || []).map((c, j) => (
                              <li key={j} className={j === q.answer_index ? "font-semibold text-[#1e8449]" : ""}>{c}{j === q.answer_index ? " ✓" : ""}</li>
                            ))}
                          </ul>
                          {q.explanation && <p className="mt-0.5 italic text-stone-400">{q.explanation}</p>}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-stone-200 pt-3">
                      <select value={pushPolicy || pack.policy_id || ""} onChange={(e) => setPushPolicy(e.target.value)}
                        className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="creator-push-policy-select">
                        <option value="">Push quiz to policy…</option>
                        {policies.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                      <Button size="sm" variant="outline" onClick={pushQuiz} disabled={pushBusy} className="border-primary/40 text-xs text-accent-foreground" data-testid="creator-push-quiz">
                        {pushBusy ? "Pushing…" : "Push quiz to policy sign-off"}
                      </Button>
                      <span className="text-[11px] text-stone-400">Questions are appended to that policy's sign-off quiz — review under Policies → Sign-off.</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 border-t border-stone-200 pt-4">
                  <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="creator-save">
                    {busy ? "Saving…" : "Save pack"}
                  </Button>
                  <Button onClick={publishReading} disabled={busy} className="bg-[#2D5A27] text-white hover:bg-[#23481e]" data-testid="creator-publish-reading">
                    {busy ? "Working…" : "Publish as read-and-sign training"}
                  </Button>
                  {pack.reading_topic_id && (
                    <span className="text-xs text-[#2D5A27]" data-testid="creator-reading-published">✓ Published — find it under Training topics</span>
                  )}
                  {readingLink && (
                    <span className="flex w-full items-center gap-2 rounded-md border border-[#2D5A27]/30 bg-[#2D5A27]/5 px-3 py-2" data-testid="creator-reading-link">
                      <input readOnly value={readingLink} className="flex-1 bg-transparent text-xs text-stone-700 outline-none" onFocus={(e) => e.target.select()} />
                      <button onClick={() => { navigator.clipboard.writeText(readingLink); toast.success("Link copied"); }}
                        className="text-xs font-semibold text-primary hover:underline" data-testid="creator-reading-copy">Copy link</button>
                    </span>
                  )}
                  <select value={attachTopic} onChange={(e) => setAttachTopic(e.target.value)}
                    className="h-9 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="creator-attach-select">
                    <option value="">Attach to topic…</option>
                    {topics.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={attach} disabled={!attachTopic} className="border-stone-300" data-testid="creator-attach-btn">Attach</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
